/* @flow */
/**
 * Manages upload/download event data, telemetry updates, and mass-upload requests.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import getflattenResponse from '../../helpers/common';

export const name = 'fileUD';

function createInitialState() {
  return {
    uploadEvents: {},
    uploadFailure: {},
    downloadEvents: {},
    downloadFailure: {},
    loadingSpinner: true,
    clickedError: false,
    uploadingSpinner: false
  };
}

export const initialState = createInitialState();

/**
 * Reducer that marks upload or download event data as loading.
 */
function setLoadingSpinnerTrue(state: Object) {
  return {
    ...state,
    loadingSpinner: true
  };
}

// UPLOAD_DATA_EVENTS
/**
 * Reducer that stores upload event objects and clears the loading state.
 */
function getUploadDataEvents(state: Object, action: Object) {
  const payload = {
    data: action.payload.data
  };
  return {
    ...state,
    uploadEvents: payload.data.objects,
    loadingSpinner: false
  };
}

// CLEAR_UPLOAD_EVENT_DATA_ON_UNMOUNT
/**
 * Reducer that clears upload event data when the upload view unmounts.
 */
function clearUploadEventsDataOnUnmount(state: Object) {
  return {
    ...state,
    uploadEvents: initialState.uploadEvents
  };
}

// RESET_UPLOAD_DATA
/**
 * Reducer that restores file upload/download state to its initial values.
 */
function resetUploadData() {
  return {
    ...initialState
  };
}

// UPLOAD_DATA_FAILURE
/**
 * Reducer that records upload/download request errors and stops loading.
 */
function setRequestFailure(state: Object, action: Object) {
  return {
    ...state,
    uploadFailure: action.payload,
    loadingSpinner: false
  };
}

// DOWNLOAD_DATA_EVENTS
/**
 * Reducer that stores download event objects and clears the loading state.
 */
function getDownloadDataEvents(state: Object, action: Object) {
  const payload = {
    data: action.payload.data
  };
  return {
    ...state,
    downloadEvents: payload.data.objects,
    loadingSpinner: false
  };
}

// CLEAR_DOWNLOAD_EVENT_DATA_ON_UNMOUNT
/**
 * Reducer that clears download event data when the download view unmounts.
 */
function clearDownloadEventsDataOnUnmount(state: Object) {
  return {
    ...state,
    downloadEvents: initialState.downloadEvents
  };
}

// fileUpload action
// FILE_UPLOADED_SUCCESS_EVENTS
/**
 * Reducer that stops the file-upload processing spinner after completion.
 */
function fileUploadSucessSpinner(state: Object) {
  return {
    ...state,
    uploadingSpinner: false
  };
}

// fileUpload action
// FILE_UPLOAD_EVENTS
/**
 * Reducer that starts the file-upload processing spinner.
 */
function fileUploadProcessingSpinner(state: Object) {
  return {
    ...state,
    uploadingSpinner: true
  };
}
/**
 * Reducer that stores the division code returned for the GAC update selection.
 */
function gacUpdateDivisionCode(state: Object, action: Object) {
  return {
    ...state,
    divisionCode: action.payload
  };
}

/**
 * Builds the reducer map consumed by the file upload/download slice.
 */
function createReducers() {
  return {
    toggleUploadProcessingSpinner: setLoadingSpinnerTrue,
    toggleDownloadProcessingSpinner: setLoadingSpinnerTrue,
    getUploadDataEvents,
    clearUploadEventsDataOnUnmount,
    resetUploadData,
    setRequestFailure,
    getDownloadDataEvents,
    clearDownloadEventsDataOnUnmount,
    fileUploadSucessSpinner,
    fileUploadProcessingSpinner,
    gacUpdateDivisionCode
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Async thunk that resets bulk-upload state when navigation leaves the GAC upload page.
 */
function resetBulkUpload() {
  return createAsyncThunk(
    `${name}/resetBulkUpload`,
    (currentPage, { getState, dispatch }) => {
      const state = getState();
      // Accept either a route key string or a navigation payload object.
      const page = typeof currentPage === 'string' ? currentPage : currentPage?.currentPage;
      // Clear upload state once the user leaves the GAC upload page.
      if (state.fileUD && page !== 'GACUPLOAD') {
        dispatch(actions.resetUploadData());
      }
    }
  );
}

/**
 * Async thunk that calls updateTelemetry to mark a report event as viewed.
 */
function updateTelemetry() {
  return createAsyncThunk(
    `${name}/updateTelemetry`,
    async ({
      eventId,
      callback
    }, { dispatch }) => {
      const query = `eventID=${eventId}`;
      const updateRequestBody = {
        message: 'Report has been viewed by the user'
      };
      dispatch(toggleSpinner(true));
      request({
        api: 'updateTelemetry',
        method: 'put',
        routeParams: { query },
        data: updateRequestBody
      })
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Async thunk that fetches user-specific download events for the selected event type.
 */
function fetchDownloadEvents() {
  return createAsyncThunk(
    `${name}/fetchDownloadEvents`,
    async ({
      eventType
    }, { getState, dispatch }) => {
      const state = getState();
      // Restrict download history to the current legacy user when available.
      const userID = state.auth?.parsedToken?.idToken?.claims?.legacy_username;
      const query = userID ? `userID=${userID.toLowerCase()}` : '';
      if (eventType !== null && eventType !== undefined) {
        // Event type indexes map UI download tabs to backend endpoint names.
        const endPointCodes = ['poPdfEvents', 'sireDeliveryEvents', 'reportEvents'];
        const endPointCode = endPointCodes[eventType || 0] || endPointCodes[0];

        dispatch(actions.clearDownloadEventsDataOnUnmount());
        dispatch(actions.toggleDownloadProcessingSpinner());
        request({
          api: endPointCode,
          routeParams: { query },
          method: 'get'
        })
          .then((response) => {
            dispatch(actions.getDownloadDataEvents(response));
          })
          .catch((error) => {
            dispatch(actions.setRequestFailure(error));
          });
      }
    }
  );
}

/**
 * Async thunk that fetches upload events for the selected upload category.
 */
function fetchUploadEvents() {
  return createAsyncThunk(
    `${name}/fetchUploadEvents`,
    async ({
      eventType
    }, { dispatch }) => {
      if (eventType !== null && eventType !== undefined) {
        // Event type indexes map UI upload tabs to backend endpoint names.
        const endPointCodes = ['gacUploadEvents', 'wipEvents', 'acceptanceEvents', 'poRejectionEvents', 'manufacturingCodeUploadEvents'];
        const endPointCode = endPointCodes[eventType || 0] || endPointCodes[0];
        dispatch(actions.clearUploadEventsDataOnUnmount());
        dispatch(actions.toggleUploadProcessingSpinner());
        request({
          api: endPointCode,
          method: 'get'
        })
          .then((response) => {
            dispatch(actions.getUploadDataEvents(response));
          })
          .catch((error) => {
            dispatch(actions.setRequestFailure(error));
          });
      }
    }
  );
}
/**
 * Extracts the division code from the first flattened GAC update result row.
 */
function getDivisionCode(data) {
  const { objects } = data;
  // The update API reports division information on the first returned PO line.
  const firstRow = objects?.[0];
  return firstRow['poLine.divisionCode'] || null;
}
/**
 * Async thunk that requests GAC update data for selected size records from the search results.
 */
function requestGacUpdate() {
  return createAsyncThunk(
    `${name}/requestGacUpdate`,
    async ({
      ids,
      selectedRecordsCount,
      callback,
      type = 'includes'
    }, { getState, dispatch }) => {
      const state = getState();
      console.log('selectedRecordsCount', selectedRecordsCount);
      const searchRequestBody = { ...state.PoSearch.body || {} };
      // Preserve existing PO search filters while excluding closed or cancelled items.
      const requestBody = {
        search: [...(searchRequestBody.search || []), {
          fieldName: 'poLine.dpomItemStatus',
          operator: '!=',
          fieldValue: ['Closed', 'Cancelled']
        }],
        fields: [
          'poHeader.poDocTypeCode',
          'poHeader.poDocTypeDescription',
          'poLine.productCode',
          'poLine.goodsAtConsolidatorDate',
          'sizes.sizePo.goodsAtConsolidatorDate',
          'sizes.sizePo.sizeDescription',
          'poLine.goodsAtConsolidatorReasonCode',
          'poLine.goodsAtConsolidatorReasonDescription',
          'sizes.sizePo.goodsAtConsolidatorReasonCode',
          'sizes.sizePo.goodsAtConsolidatorReasonDescription',
          'planning.mrgacDate',
          'poLine.divisionCode',
          'id',
          'sizes.sizeId',
          'poHeader.poNumber',
          'poLine.itemNumber',
          'sizes.scheduleLineItemNumber'
        ],
        filter: [...(searchRequestBody.filter) || []]
      };

      // Apply include/exclude size filters only when selected IDs are available.
      if (ids.length > 0) {
        requestBody.search.push({
          fieldName: 'sizes.sizeId',
          operator: type === 'includes' ? '=' : '!=',
          fieldValue: ids
        });
      }
      dispatch(toggleSpinner(true));
      request({
        api: 'GacInAppUpdate',
        method: 'post',
        data: requestBody
      })
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(getflattenResponse(response.data));
          const divisionCode = getDivisionCode(getflattenResponse(response.data));
          dispatch(actions.gacUpdateDivisionCode(divisionCode));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

// fileUpload action
/**
 * Async thunk that posts a GAC mass-upload file with the logged-in user name header.
 */
function updateFileUpload() {
  return createAsyncThunk(
    `${name}/updateFileUpload`,
    async ({
      uploadedFileData,
      callback
    }, { getState, dispatch }) => {
      dispatch(actions.fileUploadProcessingSpinner());
      const state = getState();
      const loggedInUserName = state.auth.userDetails.name;
      // Backend audit fields use the user's display name from the request headers.
      request({
        api: 'gacMassUpload',
        method: 'post',
        data: uploadedFileData,
        headers: { name: loggedInUserName }
      })
        .then((response) => {
          dispatch(actions.fileUploadSucessSpinner());
          callback(getflattenResponse(response.data));
        })
        .catch((error) => {
          dispatch(actions.fileUploadSucessSpinner());
          callback(null, error);
        });
    }
  );
}

// fileUpload action
/**
 * Async thunk that posts a PMO mass-upload file with the logged-in user name header.
 */
function pmoMassUpload() {
  return createAsyncThunk(
    `${name}/pmoMassUpload`,
    async ({
      uploadedFileData,
      callback
    }, { getState, dispatch }) => {
      dispatch(actions.fileUploadProcessingSpinner());
      const state = getState();
      const loggedInUserName = state.auth.userDetails.name;
      // Backend audit fields use the user's display name from the request headers.
      request({
        api: 'pmoMassUpload',
        method: 'post',
        data: uploadedFileData,
        headers: { name: loggedInUserName }
      })
        .then((response) => {
          dispatch(actions.fileUploadSucessSpinner());
          callback(getflattenResponse(response.data));
        })
        .catch((error) => {
          dispatch(actions.fileUploadSucessSpinner());
          callback(null, error);
        });
    }
  );
}

/**
 * Builds thunk action creators exported with the file upload/download slice actions.
 */
function createExtraActions() {
  return {
    resetBulkUpload: resetBulkUpload(), // resetRedux
    updateTelemetry: updateTelemetry(),
    fetchDownloadEvents: fetchDownloadEvents(),
    fetchUploadEvents: fetchUploadEvents(),
    requestGacUpdate: requestGacUpdate(), // posearch
    updateFileUpload: updateFileUpload(), // fileUpload Action
    pmoMassUpload: pmoMassUpload() // fileUpload Action
  };
}

export const extraActions = createExtraActions();

export const fileUDActions = { ...actions, ...extraActions };
export const fileUDReducer = slice.reducer;
